import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import React, { type FunctionComponent } from 'react';

import { type MenuPage } from './hooks/useMenuNav';

const drilldownTitleStyles = xcss({
  paddingBlockStart: 'space.200',
  paddingBlockEnd: 'space.075',
  paddingInline: 'space.200',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'default',
});

/**
 * A menu item content type that triggers in-place navigation (drilldown).
 *
 * When assigned to a `MenuItem.content`, clicking the item pushes this page
 * onto the menu stack, rendering a title header and the page's content.
 */
export type DrilldownMenu = MenuPage & {
  readonly type: 'drilldown-menu';
  title: string;
};

export const isDrilldownMenu = (value: unknown): value is DrilldownMenu =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  (value as DrilldownMenu).type === 'drilldown-menu';

type DrilldownHeaderProps = {
  title: string;
  onMouseEnter?: () => void;
  headerAction?: React.ReactNode;
};

export const DrilldownHeader: FunctionComponent<DrilldownHeaderProps> = ({
  title,
  onMouseEnter,
  headerAction,
}) => (
  <Box
    xcss={drilldownTitleStyles}
    testId="drilldown-title"
    role="presentation"
    onMouseEnter={onMouseEnter}
    onClick={(event: React.MouseEvent<HTMLElement>) => event.stopPropagation()}
  >
    <Heading size="xsmall" as="span">
      {title}
    </Heading>
    {headerAction}
  </Box>
);

/** Wrapper for key event capture during drilldown navigation. */
export const DrilldownWrapper: FunctionComponent<{
  active: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  children: React.ReactNode;
}> = ({ active, onKeyDown, children }) =>
  active ? (
    <Box role="presentation" onKeyDown={onKeyDown}>
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
